<template>
  <div
    data-testid="studio-button-group"
    :class="{
      'tw-inline-flex tw-divide-x tw-overflow-hidden': true,
      'tw-h-8 tw-rounded-lg tw-text-xs': size === 'base',
      'tw-h-6 tw-rounded-md tw-text-2xs': size === 'small',
      'tw-divide-slate-400 tw-bg-black tw-text-white dark:tw-divide-slate-600 dark:tw-bg-white dark:tw-text-black':
        variant === 'filled',
      'tw-divide-slate-200 tw-border tw-border-slate-200 tw-text-black dark:tw-divide-slate-800 dark:tw-border-slate-800 dark:tw-text-white':
        variant === 'outline'
    }"
  >
    <template v-for="(button, index) in buttons">
      <button
        v-if="!button.hidden"
        :key="button.trackingId"
        :title="button.title"
        :disabled="button.disabled || button.isLoading"
        :data-testid="button.trackingId"
        :class="[
          {
            'tw-flex tw-items-center tw-justify-center tw-font-medium focus:tw-outline-none focus:tw-ring-deep-orange-600': true,
            'tw-px-3 tw-py-2': size === 'base',
            'tw-px-2': size === 'small',
            'hover:tw-bg-deep-orange-600 dark:tw-ring-offset-black dark:hover:tw-text-white':
              variant === 'filled' && !button.disabled,
            'hover:tw-bg-deep-orange-50 hover:tw-text-deep-orange-600': variant === 'outline' && !button.disabled,
            '!tw-cursor-not-allowed tw-bg-slate-200 tw-text-slate-400 dark:tw-bg-slate-400 dark:tw-text-slate-200':
              button.disabled,
            'tw-bg-deep-orange-50 tw-text-deep-orange-600 focus:tw-ring-deep-orange-600 dark:tw-ring-offset-black dark:hover:tw-text-white':
              showActiveButton && activeButtonIndex === index
          },
          button.extraClasses
        ]"
        @click="e => handleButtonClick(button, e)"
      >
        <StudioSpinner v-if="button.isLoading" data-testid="studio-spinner" :coverContainer="false" size="xxsmall" />
        <template v-else>
          <div
            v-if="button.iconComponent"
            :class="{
              'tw-flex tw-items-center tw-justify-center': true,
              '-tw-mx-[5px]': size === 'small',
              '-tw-mx-[7px]': size === 'base',
              '-tw-ml-1 tw-mr-1': button.label,
              'tw-h-4 tw-w-4': size === 'small',
              'tw-h-5 tw-w-5': size === 'base'
            }"
          >
            <component :is="button.iconComponent" />
          </div>
          <div v-if="button.label">{{ button.label }}</div>
        </template>
      </button>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import StudioSpinner from './StudioSpinner.vue';

import analytics from '@/analytics';

export type StudioButtonGroupButtonProps = {
  label?: string;
  disabled?: boolean;
  title?: string;
  hidden?: boolean;
  trackingId: string;
  iconComponent?: any;
  isLoading?: boolean;
  extraClasses?: string;

  clickHandler: (e: Event | undefined) => void;
  meta?: {
    routeName?: string;
  };
};

export default defineComponent({
  name: 'StudioButtonGroup',
  components: {
    StudioSpinner
  },
  props: {
    variant: {
      type: String as () => 'filled' | 'outline',
      default: 'filled',
      validator: (value: string) => ['filled', 'outline'].includes(value)
    },
    buttons: {
      type: Array as () => StudioButtonGroupButtonProps[],
      required: true,
      validator: (value: StudioButtonGroupButtonProps[]) => value.length >= 1
    },
    size: {
      type: String as () => 'base' | 'small',
      default: 'base',
      validator: (value: string) => ['base', 'small'].includes(value)
    },
    activeButtonIndex: {
      type: Number,
      default: 0
    },
    showActiveButton: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleButtonClick(button: StudioButtonGroupButtonProps, e: MouseEvent) {
      if (button.trackingId) {
        analytics.track('button_click', {
          button_name: button.trackingId
        });
      }

      button.clickHandler(e);
    }
  }
});
</script>
